import { Injectable } from '@angular/core';
import { SymplifyItem, SymplifyMetaData } from '../models/symplify-item';
import { HelpsService } from '../../../../../src/modules/shared/services/helps.service';
import { PriceWeb } from '../models/price-web';
import { CartPrice } from '../models/cart-price';
import { lastValueFrom } from 'rxjs';
import { Cart } from '../models/cart';
import { environment } from '../../../../../environments';
import { ClientDetails } from '../models/client-details';
import URLValidator from '../utils/URLValidator';
import { ClarityService } from '../component/microsoftClarity/clarity.service';
import { ClarityEventType } from '../component/microsoftClarity/clarity.types';
import { Page } from '../models/page';

declare const carma: any;

@Injectable({
    providedIn: 'root',
})
export class SymplifyCartService {
    client: ClientDetails;
    active = false;
    webInteractionsActive = false;

    constructor(private clarityService: ClarityService) {}

    async init(lang) {
        // Script panier abandonné (historique)
        let id = '';
        if (lang === 'fr') {
            id = environment.symplify.abandonedCartFr;
        } else {
            id = environment.symplify.abandonedCartEn;
        }
        if (id) {
            this.active = true;
            await lastValueFrom(
                HelpsService.injectScriptObservable('//mimgs.s3.amazonaws.com/scripts/ac.js', { id: 'carma_ac', 'data-cid': String(id) }),
            );
        } else {
            this.active = false;
        }
    }

    async initWebTracking(lang) {
        if (
            !environment.symplify.webInteractionServer ||
            !environment.symplify.webInteractionToken ||
            !environment.symplify.webInteractionUrl
        ) {
            this.webInteractionsActive = false;
            return;
        }
        this.webInteractionsActive = true;
        // Injecter le script de configuration Web Interactions
        try {
            await HelpsService.injectScript('https://d3mi6d1ao3fzsg.cloudfront.net/tracking.min.js', {
                id: 'carma-tracking',
                'data-server': environment.symplify.webInteractionServer,
                'data-token': environment.symplify.webInteractionToken,
                'data-url': environment.symplify.webInteractionUrl,
            });
            this.webInteractionsActive = true;
        } catch (error) {
            console.error('Failed to initialize Symplify Web Interactions', error);
        }
    }

    // --- Fonctions Web Interactions Symplify ---
    track(eventName: string, eventData?: any) {
        if (!this.webInteractionsActive) {
            return;
        }
        try {
            if (carma && carma.tracking && typeof carma.tracking.track === 'function') {
                if (eventData) {
                    carma.tracking.track(eventName, eventData);
                } else {
                    carma.tracking.track(eventName);
                }
            }
        } catch (e) {}
    }

    identifyUser(user: any) {
        if (!this.webInteractionsActive) {
            return;
        }
        if (!user) {
            return;
        }
        try {
            if (carma && carma.tracking && typeof carma.tracking.identify === 'function') {
                carma.tracking.identify(user);
            }
        } catch (e) {}
    }

    trackVisitedPage(page: Page) {
        this.track('visited-page', { page: page.title, id: page.id });
    }

    trackVisitedPageTitle(title: string) {
        this.track('visited-page', { page: title });
    }

    trackBrowsedCategory(category: string) {
        this.track('browsed-category', { category });
    }

    // --- Fonctions historiques panier abandonné (inchangées) ---
    addAllCart(cart: Cart) {
        if (!this.active) {
            return;
        }
        try {
            if (cart) {
                cart?.cartElements.forEach((elm) => {
                    this.addToCartPrice(elm.prices);
                });
            }
        } catch (e) {
            console.log(e);
        }
    }

    registerEmail(email) {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.registerEmail(email);
            this.identifyUser(email);
        } catch (e) {
            console.log(e);
        }
    }

    addMetaData(metaData: SymplifyMetaData) {
        if (!this.active) {
            return;
        }
        try {
            for (const [key, value] of Object.entries(metaData)) {
                const t = {};
                t[key] = value;
                carma.ac.addMetaData(t);
            }
        } catch (e) {
            console.log(e);
        }
    }

    addToCart(item: SymplifyItem) {
        if (!this.client || !this.active) {
            return;
        }
        try {
            carma.ac.addToCart(item);
        } catch (e) {
            console.log(e);
        }
    }

    addToCartPrice(priceWebs: PriceWeb[] | CartPrice[] | any[]) {
        if (!(environment.systemValues === 'tkp')) {
            return;
        }
        if (!this.client || !this.active) {
            return;
        }
        try {
            let metaData: SymplifyMetaData = {};
            let items = [];
            let hasAbandonedCartActive = false;
            priceWebs.forEach((value) => {
                if (value.abandonedCartActive) {
                    hasAbandonedCartActive = true;
                    if (value.pageUrl && value.pageUrl.length > 10 && value.title) {
                        const item: SymplifyItem = {
                            id: String(value.id),
                            amount: value.qty * ((value.price + value.serviceCharges) / 100),
                            quantity: value.qty,
                            description: value.name,
                            name: value.name,
                        };
                        items.push(item);
                        //this.addToCart(item);
                        metaData = {
                            image_url: value.imageUrl,
                            sale_url: this.addOrReplaceUrlParam(
                                value.cleanPageUrl ? value.cleanPageUrl : value.pageUrl,
                                't',
                                'abandonedCart',
                            ),
                            lieu_nom: value.venue,
                            show_date: value.date,
                            show_title: value.title,
                            eventId: value.eventId,
                        };
                    }
                }
            });
            if (Object.keys(metaData).length > 0 && metaData.sale_url && items.length > 0) {
                if (URLValidator.isValid(metaData.sale_url)) {
                    items.forEach((item) => this.addToCart(item));
                    delete metaData.eventId;
                    this.addMetaData(metaData);
                    this.registerEmail(this.client.email);
                } else {
                    console.error('Symplify: URL non valide', metaData.sale_url);
                    this.clarityService.trackErrorByType(
                        ClarityEventType.ERROR_SYMPLIFY_BY_SHOW,
                        metaData,
                        'URL non valide' + metaData?.eventId,
                    );
                }
            } else {
                if (hasAbandonedCartActive) {
                    const data: any = {};
                    data.url = window.location.href;
                    if (priceWebs.length > 0) {
                        data.eventId = priceWebs[0].eventId;
                        data.pageRefId = priceWebs[0].pageRefId;
                        data.showId = priceWebs[0].showId;
                        data.comboId = priceWebs[0].comboId;
                    }
                    this.clarityService.trackErrorByType(
                        ClarityEventType.ERROR_SYMPLIFY,
                        {
                            ...data,
                            ...metaData,
                        },
                        'URL non valide 2 ',
                    );
                    return;
                }
            }
        } catch (e) {
            console.log(e);
            this.clarityService.trackErrorByType(ClarityEventType.ERROR_SYMPLIFY, {}, 'URL non valide 3 catch');
        }
    }

    addOrReplaceUrlParam(url: string, paramName: string, paramValue: string): string {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        params.set(paramName, paramValue);
        urlObj.search = params.toString();
        return urlObj.toString();
    }

    removeFromCart(item: SymplifyItem) {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.removeFromCart(item);
        } catch (e) {
            console.log(e);
        }
    }

    removeFromCartPrice(cartPrices: [CartPrice]) {
        if (!this.active) {
            return;
        }
        try {
            cartPrices.forEach((value) => {
                const item: SymplifyItem = {
                    id: String(value.id),
                    amount: value.cartDetailToken.length * ((value.price + value.serviceCharges) / 100),
                    quantity: value.cartDetailToken.length,
                    description: value.name,
                    name: value.name,
                };
                this.removeFromCart(item);
            });
        } catch (e) {
            console.log(e);
        }
    }

    setCart(cartData, totalAmount) {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.setCart(cartData, totalAmount);
        } catch (e) {
            console.log(e);
        }
    }

    resetCart() {
        if (!this.active) {
            return;
        }
        try {
            //carma.ac.resetCart();
        } catch (e) {
            console.log(e);
        }
    }

    purchaseCompleted() {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.purchaseCompleted();
        } catch (e) {
            console.log(e);
        }
    }
}
